import { useEffect, useRef, useSyncExternalStore } from 'react';
import { iniciar } from '@/lib/centralita';

/**
 * El iframe del CCP, anclado al layout y nunca desmontado.
 *
 * El problema que resuelve: el iframe sostiene la conexión WebRTC de la
 * llamada, así que quitarlo del DOM la corta. Pero la pestaña Centralita es una
 * ruta, y las rutas se desmontan al navegar. Si el CCP viviera dentro de ella,
 * salir de la pestaña con una llamada en curso la cortaría.
 *
 * La salida es separar dónde vive de dónde se ve. Vive aquí, en un contenedor
 * `fixed` colgado del layout raíz, que no se desmonta jamás. Se ve donde diga
 * `apuntarA()`: la pestaña Centralita registra un hueco vacío y este contenedor
 * se coloca justo encima, midiéndolo. Cuando no hay hueco registrado, se aparta
 * fuera de la pantalla en vez de ocultarse con `display:none`, porque un iframe
 * sin caja deja de reproducir audio en algunos navegadores.
 */

// ── Registro del hueco donde toca enseñarse ──────────────────────────────────

let hueco: HTMLElement | null = null;
const oyentes = new Set<() => void>();

function avisar() {
  oyentes.forEach((fn) => fn());
}

/** La pestaña Centralita llama a esto con su hueco; al salir, con null. */
export function apuntarA(elemento: HTMLElement | null) {
  hueco = elemento;
  avisar();
}

function suscribir(fn: () => void) {
  oyentes.add(fn);
  return () => oyentes.delete(fn);
}

const leerHueco = () => hueco;

/**
 * Los navegadores móviles no soportan el softphone WebRTC de Connect: solo
 * Chrome, Edge y Firefox de escritorio. Conviene saberlo antes de cargar nada,
 * para poder explicarlo en vez de enseñar un iframe que no va a funcionar.
 */
export function soportaCentralita(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const esMovil = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
  const esSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return !esMovil && !esSafari;
}

export default function AnclaCCP() {
  const caja = useRef<HTMLDivElement>(null);
  const destino = useSyncExternalStore(suscribir, leerHueco, leerHueco);

  // Arranca el CCP una sola vez. El módulo lleva su propio cerrojo, así que el
  // doble montaje de StrictMode en desarrollo no lo duplica.
  useEffect(() => {
    if (caja.current && soportaCentralita()) iniciar(caja.current);
  }, []);

  // Coloca el contenedor sobre el hueco y lo mantiene ahí mientras cambie la
  // pantalla. Se mide en cada cambio en vez de calcularlo una vez porque el
  // hueco se mueve al desplegar el menú lateral, al girar el móvil o al
  // aparecer un aviso encima.
  useEffect(() => {
    const el = caja.current;
    if (!el) return;

    const colocar = () => {
      if (!destino) {
        // Aparcado: fuera de la vista pero con caja real, para no perder el audio.
        el.style.cssText =
          'position:fixed;left:-10000px;top:0;width:400px;height:600px;opacity:0;pointer-events:none;z-index:0;';
        return;
      }
      const r = destino.getBoundingClientRect();
      el.style.cssText =
        `position:fixed;left:${r.left}px;top:${r.top}px;width:${r.width}px;height:${r.height}px;` +
        'opacity:1;pointer-events:auto;z-index:10;border-radius:12px;overflow:hidden;';
    };

    colocar();
    const observador = new ResizeObserver(colocar);
    if (destino) observador.observe(destino);
    observador.observe(document.body);
    window.addEventListener('scroll', colocar, true);
    window.addEventListener('resize', colocar);
    return () => {
      observador.disconnect();
      window.removeEventListener('scroll', colocar, true);
      window.removeEventListener('resize', colocar);
    };
  }, [destino]);

  // El iframe que crea Streams se cuelga de este div. Nunca se desmonta.
  return <div ref={caja} aria-label="Panel de la centralita" />;
}
